/** lag-pagination
 * 
 *  OPTIONAL ATTRIBUTES
 *      show-first          // Shows the first page (<<) button.
 *      show-last           // Shows the last page (>>) button.
 *      show-selector       // Shows the drop down page selector.
 *      show-size           // Shows the page size buttons (10,20,50,100).
 *      show-size-all       // Shows the page size 'All' button.
 *      show-always         // Shows the paging bar even if only 1 page.
 *
 *  REQUIRED ATTRIBUTES
 *      pagination-data="{{paginationData}}" // Two-way bind lag-pagination data to a property in your containing component (and the lag-pagination elements together).
 *
 *  EXAMPLE USAGE
 *      <lag-pagination pagination-data="{{paginationData}}" show-first show-last show-selector show-size show-size-all></lag-pagination>
 *          ...paging content...
 *      <lag-pagination pagination-data="{{paginationData}}" show-first show-last show-selector show-size show-size-all></lag-pagination>
 *  (JS)
 *      static get properties() { return { paginationData: Object }; } // The property that you two-way bind to the lag-pagination elements (see above HTML example).
 *      static get observers() { return ['_paginationDataChanged(paginationData.*)']; } // Observe changes to the pagination data sub-properties so you can update your data.
 *      _paginationDataChanged() {
 *          ...update stuff...
 *          this._paginationData.totalCount = newTotalUnitCount; // Allows lag-pagination to know how many pages should be shown.
 *      }
 *
 *  PROPERTIES
 *      paginationData: {
 *          page,
 *          perPage,
 *          totalCount
 *      }
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { GestureEventListeners } from '@polymer/polymer/lib/mixins/gesture-event-listeners.js'; // https://www.polymer-project.org/3.0/docs/devguide/gesture-events
import { Localizer } from '../extendibles/Localizer.js';

import '@polymer/paper-menu-button/paper-menu-button.js';
import '@polymer/paper-listbox/paper-listbox.js';
import '@polymer/paper-item/paper-item.js';
import '@polymer/iron-icon/iron-icon.js';

import '../../assets/icons.js';

import { defineElement } from '../../utility/defineElement.js';
import { debounce } from '../../utility/debounce.js';
import { template } from './lag-pagination.html.js';

defineElement(class extends Localizer(GestureEventListeners(PolymerElement)) {

    constructor() {
        super();

        this._PERPAGE_MAX = 10000;
        this._PERPAGE_RANGE = [20, 40];
        this._PAGESRANGE_SIZE = 7;
    }

    static get is() { return 'lag-pagination'; }

    static get template() { return template }

    static get properties() {
        return {
            heading: {
                type: String,
                value: null
            },
            paginationData: {
                type: Object,
                notify: true,
                value: {
                    page: 1,
                    perPage: 20,
                    totalCount: 0
                }
            },
            showFirst: {
                type: Boolean,
                value: false
            },
            showLast: {
                type: Boolean,
                value: false
            },
            showSelector: {
                type: Boolean,
                value: false
            },
            showSize: {
                type: Boolean,
                value: false
            },
            perpageRange: {
                type: Array,
                observer:'_observePerpageRangeChange'
            },
            showSizeAll: {
                type: Boolean,
                value: false
            },
            showAlways: {
                type: Boolean,
                value: false
            },
            showTotalCount: {
                type: Boolean,
                value: false
            },

            // Private properties...

            _disablePrev: {
                type: Boolean,
                computed: '_computeDisablePrev(paginationData.page)'
            },
            _disableNext: {
                type: Boolean,
                computed: '_computeDisableNext(_pageCount, paginationData.page)'
            },
            _disableSelector: {
                type: Boolean,
                computed: '_computeDisableSelector(_pageCount)'
            },
            _pageCount: {
                type: Number,
                computed: '_computePageCount(paginationData.totalCount, paginationData.perPage)'
            },
            _currentPageRange: {
                type: Array,
                value: [],
                computed: '_computeCurrentPageRange(_pageCount, paginationData.page)'
            },
            _completePageRange: {
                type: Array,
                value: [],
                computed: '_computeCompletePageRange(_pageCount)'
            }
        };
    }

    static get observers() {
        return ['_observePaginationData(paginationData, paginationData.page, paginationData.perPage, paginationData.totalCount)'];
    }

    _observePerpageRangeChange() {
        if (this.perpageRange && this.perpageRange.length > 0)
            this._PERPAGE_RANGE = this.perpageRange;
    }

    _observePaginationData() {
        this._updateActivePage();
    }

    _computeDisablePrev(page) {
        return parseInt(page) === 1;
    }
    _computeDisableNext(pageCount, page) {
        return parseInt(page) === pageCount;
    }
    _computeDisableSelector(pageCount) {
        return parseInt(pageCount) <= this._PAGESRANGE_SIZE;
    }

    _computePageCount(totalCount, perPage) {
        totalCount = parseInt(totalCount) || 1;
        perPage = parseInt(perPage) || 1;

        // Protect against crazy high counts (which could freeze the browser/UI).
        // Shouldn't ever need this many pages. Even cloud-search results only allow up to 10k results.
        if (totalCount > 10000)
            totalCount = 10000;

        if (this.showAlways)
            this.hidden = false;
        else {
            const minPageSize = (perPage > 1 ? perPage : this._PERPAGE_RANGE[0]);
            const canOnlyHaveSinglePage = totalCount <= minPageSize;
            this.hidden = canOnlyHaveSinglePage; // Hide the paging if only 1 page is possible.
        }

        return Math.ceil(totalCount / perPage);
    }

    _computeCurrentPageRange(pageCount, page) {
        pageCount = parseInt(pageCount) || 1;
        page = parseInt(page) || 1;

        const paginations = [],
            midSize = Math.floor(this._PAGESRANGE_SIZE / 2);
        let rangeMin, rangeMax;

        // Figure out and return an array of pages (integers) that will be displayed
        // as page numbers that can be jumped to (ie a range of adjacent/inclusive pages).

        if (pageCount <= this._PAGESRANGE_SIZE) {
            // Less pages than range-size, so show first set of pages up to pageCount.
            rangeMin = 1;
            rangeMax = pageCount;
        }
        else if (page <= midSize) {
            // Current page is before the midSize threshold, so show first set of pages up to pages-range size.
            rangeMin = 1;
            rangeMax = this._PAGESRANGE_SIZE;
        }
        else if (page >= pageCount - midSize) {
            // At the end of the pages, so show the last set of pages.
            rangeMin = pageCount - this._PAGESRANGE_SIZE + 1;
            rangeMax = pageCount;
        }
        else {
            // Otherwise we're in the middle somwhere.
            rangeMin = page - midSize;
            rangeMax = page + midSize;
        }

        // Add the page numbers.
        for (let i = rangeMin; i <= rangeMax; i++) {
            paginations.push(i);
        }

        return paginations;
    }
    _computeCompletePageRange(pageCount) {
        pageCount = parseInt(pageCount) || 1;

        const paginations = [];
        for (let i = 1; i <= pageCount; ++i) {
            paginations.push(i);
        }
        return paginations;
    }

    reset(){        
        this.setPage(1);
    }

    _ifThen(conditionA, conditionB, then) {
        return conditionA == conditionB ? then : null;
    }

    _handleFirstPage() {
        this.setPage(1);
    }
    _handleLastPage() {
        this.setPage(parseInt(this._pageCount));
    }
    _handlePrevPage() {
        this.setPage(parseInt(this.paginationData.page) - 1);
    }
    _handleNextPage() {
        this.setPage(parseInt(this.paginationData.page) + 1);
    }
    _handleSelectPage(e) {
        const page =
            (e.model && e.model.item) ? e.model.item
            : ((e.target && e.target.selectedItem) ? e.target.selectedItem.textContent
            : 0);

        this.setPage(parseInt(page, 10));
    }
    _handleSelectSize(e) {
        const newPerPage = parseInt(e.model.item, 10),
            newPageCount = Math.ceil(this.paginationData.totalCount / newPerPage);
        let newCurrentPage = Math.max(1, Math.ceil(this.paginationData.page * this.paginationData.perPage / newPerPage));

        if (newCurrentPage < 1) {
            newCurrentPage = 1;
        }
        if (newCurrentPage > newPageCount) {
            newCurrentPage = newPageCount;
        }

        this.paginationData.page = newCurrentPage;
        this.paginationData.perPage = newPerPage;
        this.notifyPath('paginationData.perPage');

        this._fireChanged();
    }
    _handleSelectSizeAll() {
        this.paginationData.page = 1;
        this.paginationData.perPage = this._PERPAGE_MAX;
        this.notifyPath('paginationData.perPage');

        this._fireChanged();
    }

    _updateActivePage() {
        debounce('lag-pagination._updateActivePage', 200, () => {
            const list = this.$.paginationlist.children;

            for (let i = 0; i < list.length; i++) {
                const listElement = list[i],
                    isCurrentPage = listElement.classList.contains('page') && listElement.dataItem === parseInt(this.paginationData.page),
                    isItemsPerPage = listElement.classList.contains('itemsPerPage'),
                    isItemsPerPageCurrent = isItemsPerPage && listElement.dataItem === this.paginationData.perPage,
                    isItemsPerPageMax = isItemsPerPage && this.paginationData.perPage === this._PERPAGE_MAX && listElement.classList.contains('itemsPerPage-all');

                if (isCurrentPage || isItemsPerPageCurrent || isItemsPerPageMax) {
                    listElement.classList.add('active');
                }
                else {
                    listElement.classList.remove('active');
                }
            }
        });
    }

    _fireChanged() {
        this.dispatchEvent(new Event('change'));
    }

    setPage(page) {
        if (typeof page !== 'number') {
            page = parseInt(page, 10);
        }

        if (page < 1) return;
        if (page > this._pageCount) return;

        if (page !== this.paginationData.page) {
            this.set('paginationData.page', page);

            this._fireChanged();
        }
    }
});